// User-friendly editor for a column's lookup filter. Renders an inline
// row-per-condition builder by default, with an "Advanced (SQL)" toggle
// that exposes the raw WHERE-clause textarea for power users.
//
// Storage model:
// - `lookup_filter_rules` holds the structured form (preferred). When
//   present, the runtime compiles it to SQL + a literal map.
// - `lookup_filter` holds a hand-edited SQL fragment (advanced mode).
//   The runtime falls back to this when no rules exist.
// Only one of the two is kept at a time — toggling modes clears the
// other so the column never carries a stale fragment.

import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import type {
  ColumnDef, LookupFilterCondition, LookupFilterOp,
  LookupFilterRules, LookupFilterSource,
} from '../txn/types'
import { LOOKUP_NULL_OPS, LOOKUP_OP_LABELS } from '../txn/types'
import type { DesignerCatalog } from './catalog'
import { useKitConfirm } from './KitModal'
import { SqlBuilderButton } from './sqlBuilder'

interface ColumnSummary { column_name: string; data_type?: string }

interface Props {
  // Column carrying the lookup. Read its current rules / raw SQL.
  column: ColumnDef
  // Form columns (current sql_model) — values bind to {form.X}.
  formColumns: string[]
  // Header (sql_models[0]) columns — values bind to {header.X}. Pass an
  // empty array for the header form itself; the editor will skip the
  // "Header" source option.
  headerColumns: string[]
  // Project slug (the host's catalog is bound to it).
  project: string
  // Where the lookup table's columns come from for the "lookup column"
  // autocomplete (designerKit/catalog). Absent ⇒ the user free-types.
  catalog?: DesignerCatalog
  // Patch back to the column. Only the two filter fields are touched;
  // other column properties pass through unchanged.
  onChange: (patch: {
    lookup_filter_rules?: LookupFilterRules | undefined
    lookup_filter?: string | undefined
  }) => void
}

// Pull the table portion from a "TABLE(DISP, DATA)" spec. Returns ''
// when the spec is empty or malformed — caller silently falls back to
// no autocomplete.
function parseLookupTable(spec: string | undefined): string {
  if (!spec) return ''
  const m = spec.trim().match(/^([A-Za-z_][A-Za-z0-9_]*)\s*\(/)
  return m ? m[1] : ''
}

const ALL_OPS: LookupFilterOp[] = [
  'eq', 'ne', 'gt', 'lt', 'gte', 'lte',
  'contains', 'starts_with', 'ends_with',
  'is_null', 'is_not_null',
]

function emptyCondition(): LookupFilterCondition {
  return { col: '', op: 'eq', source: 'form', value: '' }
}

const rowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) auto minmax(0, 1fr) auto',
  gap: 6,
  alignItems: 'center',
  marginBottom: 6,
}

const cellStyle: React.CSSProperties = {
  width: '100%',
  padding: '4px 6px',
  background: 'var(--color-bg)',
  border: '1px solid var(--color-border)',
  borderRadius: 4,
  color: 'var(--color-text)',
  fontSize: '0.8125rem',
  minWidth: 0,
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '0.8125rem',
  color: 'var(--color-text-secondary)',
  fontWeight: 600,
  marginBottom: 4,
  marginTop: 6,
}

const helpStyle: React.CSSProperties = {
  fontSize: '0.8125rem',
  color: 'var(--color-text-secondary)',
  marginTop: 4,
  lineHeight: 1.4,
}

const linkBtnStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: 'var(--color-accent)',
  cursor: 'pointer',
  fontSize: '0.8125rem',
  padding: 0,
  textDecoration: 'underline',
}

const sourceColon = ': '

export default function LookupFilterEditor({
  column, formColumns, headerColumns, project, onChange, catalog,
}: Props) {
  const hasRules = !!column.lookup_filter_rules
  const hasRawSql = ((column.lookup_filter || '').trim().length > 0)
  // Prefer rules mode by default. Show advanced only when raw SQL exists
  // with no rules (legacy / hand-edited fragment).
  const [advanced, setAdvanced] = useState<boolean>(hasRawSql && !hasRules)
  const { confirm, confirmDialog } = useKitConfirm(catalog)

  // Lookup table column suggestions, fetched from the database designer.
  const datalistId = useId()
  const lookupTable = useMemo(
    () => parseLookupTable(column.lookup),
    [column.lookup],
  )
  const [lookupCols, setLookupCols] = useState<ColumnSummary[]>([])
  const reqSeq = useRef(0)

  useEffect(() => {
    if (!catalog || !lookupTable) { setLookupCols([]); return }
    const seq = ++reqSeq.current
    catalog.columns(lookupTable)
      .then((res) => {
        if (seq !== reqSeq.current) return
        const cols: ColumnSummary[] = Array.isArray(res) ? res : []
        setLookupCols(cols)
      })
      .catch(() => {
        if (seq !== reqSeq.current) return
        setLookupCols([])
      })
  }, [project, catalog, lookupTable])

  const rules: LookupFilterRules = column.lookup_filter_rules
    || { conditions: [] }

  const patchRules = (next: LookupFilterRules) => {
    // Drop legacy raw SQL whenever the user touches the structured rules
    // — keeps the storage model "exactly one of rules/sql is live".
    onChange({
      lookup_filter_rules: next,
      lookup_filter: '',
    })
  }

  const updateCondition = (i: number, patch: Partial<LookupFilterCondition>) => {
    const conditions = [...(rules.conditions || [])]
    conditions[i] = { ...conditions[i], ...patch }
    patchRules({ conditions })
  }

  const addCondition = () => {
    patchRules({
      conditions: [...(rules.conditions || []), emptyCondition()],
    })
  }

  const removeCondition = (i: number) => {
    const conditions = (rules.conditions || []).filter((_, idx) => idx !== i)
    patchRules({ conditions })
  }

  // Toggle between Simple ↔ Advanced. Switching to Advanced compiles the
  // current rules into a SQL preview so the user has something to start
  // editing; switching back clears the raw SQL.
  const switchToAdvanced = async () => {
    const { compileLookupFilter } = await import('../txn/types')
    const compiled = compileLookupFilter(rules)
    // The compiled SQL keeps {form.X} / {header.X} placeholders but
    // substitutes {lit.LN} with the literal value escaped as a quoted
    // SQL literal — so the advanced view is human-readable. The runtime
    // re-parses {form/header/lit} the same way on send.
    let sql = compiled.sql
    Object.entries(compiled.literals).forEach(([k, v]) => {
      // Replace each {lit.k} placeholder with an escaped literal so the
      // advanced editor is human-readable. Literals come back through
      // the placeholder map on the next compile — see compileLookupFilter.
      const lit = formatSqlLiteral(v)
      sql = sql.split(`{lit.${k}}`).join(lit)
    })
    onChange({
      lookup_filter: sql,
      lookup_filter_rules: undefined,
    })
    setAdvanced(true)
  }

  const switchToSimple = async () => {
    // We deliberately do not try to parse arbitrary SQL back into
    // structured conditions. The user opts in: clearing the raw SQL is
    // the explicit signal that they want the builder. Confirm before
    // dropping non-empty SQL so a misclick can't lose work.
    if (hasRawSql) {
      const ok = await confirm({
        title: 'Switch to the simple builder?',
        message: 'Your hand-written SQL will be cleared.',
        confirmLabel: 'Switch',
      })
      if (!ok) return
    }
    onChange({
      lookup_filter: '',
      lookup_filter_rules: { conditions: rules.conditions || [] },
    })
    setAdvanced(false)
  }

  // No lookup configured → nothing to filter against.
  if (!(column.lookup || '').trim()) return null

  return (
    <div style={{ marginTop: 6 }}>
      {confirmDialog}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <label style={{ ...labelStyle, marginTop: 0 }}>
          Lookup Filter (optional)
        </label>
        {advanced ? (
          <button
            type="button"
            style={linkBtnStyle}
            onClick={() => void switchToSimple()}
            title="Use the structured condition builder"
          >← Simple</button>
        ) : (
          <button
            type="button"
            style={linkBtnStyle}
            onClick={switchToAdvanced}
            title="Edit the raw WHERE-clause SQL"
          >Advanced (SQL) →</button>
        )}
      </div>

      {advanced ? (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
            <SqlBuilderButton value={column.lookup_filter || ''} mode="where" project={project}
              contextTables={lookupTable ? [lookupTable] : undefined} title="Lookup filter"
              onChange={(sql) => onChange({ lookup_filter: sql, lookup_filter_rules: undefined })} />
          </div>
          <textarea
            style={{
              ...cellStyle,
              minHeight: 56,
              resize: 'vertical',
              fontFamily: 'monospace',
            }}
            value={column.lookup_filter || ''}
            placeholder="active = true AND dept_code = {form.DEPT_CODE}"
            onChange={e => onChange({
              lookup_filter: e.target.value,
              lookup_filter_rules: undefined,
            })}
          />
          <div style={helpStyle}>
            Use <code style={{ color: 'var(--color-text)' }}>{'{form.COL}'}</code> for a
            current-form value and{' '}
            <code style={{ color: 'var(--color-text)' }}>{'{header.COL}'}</code> for a
            header value. Values are bound as parameters. Semicolons,
            comments, and multi-statement clauses are rejected by the
            server.
          </div>
        </>
      ) : (
        <>
          {/* Shared datalist of lookup-table columns. Cheaper than 1
              datalist per row and the suggestions are the same set. */}
          <datalist id={datalistId}>
            {lookupCols.map(c => (
              <option key={c.column_name} value={c.column_name}>
                {c.data_type || ''}
              </option>
            ))}
          </datalist>

          {(rules.conditions || []).length === 0 ? (
            <div style={{
              ...helpStyle, marginTop: 0, marginBottom: 6,
              fontStyle: 'italic',
            }}>
              No filter — the lookup returns every row. Add a condition
              to narrow it down using a current-form, header-form, or
              literal value.
            </div>
          ) : null}

          {(rules.conditions || []).map((c, i) => (
            <ConditionRow
              key={i}
              condition={c}
              datalistId={datalistId}
              formColumns={formColumns}
              headerColumns={headerColumns}
              onChange={(patch) => updateCondition(i, patch)}
              onRemove={() => removeCondition(i)}
            />
          ))}

          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={addCondition}
            style={{ marginTop: 4, padding: '3px 8px', fontSize: '0.8125rem' }}
          >
            <Plus size={11} /> Add condition
          </button>

          {(rules.conditions || []).length > 1 && (
            <div style={helpStyle}>
              All conditions are combined with <strong>AND</strong>.
              Switch to <em>Advanced (SQL)</em> to write OR / parentheses /
              other shapes by hand.
            </div>
          )}
        </>
      )}
    </div>
  )
}

function ConditionRow({
  condition, datalistId, formColumns, headerColumns, onChange, onRemove,
}: {
  condition: LookupFilterCondition
  datalistId: string
  formColumns: string[]
  headerColumns: string[]
  onChange: (patch: Partial<LookupFilterCondition>) => void
  onRemove: () => void
}) {
  const needsValue = !LOOKUP_NULL_OPS.has(condition.op)

  // When switching to a NULL op, clear the value so a future switch
  // back doesn't surface a stale literal/column reference.
  const setOp = (op: LookupFilterOp) => {
    if (LOOKUP_NULL_OPS.has(op)) {
      onChange({ op, value: '' })
    } else {
      onChange({ op })
    }
  }

  // When switching source, the meaning of `value` changes (column name
  // vs literal vs nothing). Clear it to avoid mis-bindings.
  const setSource = (source: LookupFilterSource) => {
    onChange({ source, value: '' })
  }

  return (
    <div style={rowStyle}>
      <input
        list={datalistId}
        style={cellStyle}
        value={condition.col}
        placeholder="lookup column"
        title="Column of the lookup table to filter on"
        onChange={e => onChange({ col: e.target.value })}
      />
      <select
        style={{ ...cellStyle, cursor: 'pointer' }}
        value={condition.op}
        onChange={e => setOp(e.target.value as LookupFilterOp)}
      >
        {ALL_OPS.map(op => (
          <option key={op} value={op}>{LOOKUP_OP_LABELS[op]}</option>
        ))}
      </select>

      {needsValue ? (
        <select
          style={{ ...cellStyle, cursor: 'pointer', width: 'auto' }}
          value={condition.source}
          onChange={e => setSource(e.target.value as LookupFilterSource)}
          title="Where the comparison value comes from"
        >
          <option value="form">Form{sourceColon}</option>
          {headerColumns.length > 0 && (
            <option value="header">Header{sourceColon}</option>
          )}
          <option value="literal">Literal{sourceColon}</option>
        </select>
      ) : (
        <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
          {/* spacer so the grid columns stay aligned */}
        </span>
      )}

      {needsValue ? (
        <ValuePicker
          condition={condition}
          formColumns={formColumns}
          headerColumns={headerColumns}
          onChange={(v) => onChange({ value: v })}
        />
      ) : (
        <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }} />
      )}

      <button
        type="button"
        onClick={onRemove}
        title="Remove condition"
        style={{
          background: 'transparent', border: 'none',
          cursor: 'pointer', color: 'var(--color-text-secondary)',
          padding: 2, lineHeight: 1,
        }}
      >
        <Trash2 size={13} />
      </button>
    </div>
  )
}

function ValuePicker({
  condition, formColumns, headerColumns, onChange,
}: {
  condition: LookupFilterCondition
  formColumns: string[]
  headerColumns: string[]
  onChange: (value: string) => void
}) {
  if (condition.source === 'form') {
    return (
      <select
        style={{ ...cellStyle, cursor: 'pointer' }}
        value={condition.value}
        onChange={e => onChange(e.target.value)}
      >
        <option value="">— select column —</option>
        {formColumns.map(n => (
          <option key={n} value={n}>{n}</option>
        ))}
        {/* Keep an unknown stored value visible so the user can see what
            was selected before, even if the column was renamed. */}
        {condition.value && !formColumns.includes(condition.value) && (
          <option value={condition.value}>{condition.value} (missing)</option>
        )}
      </select>
    )
  }
  if (condition.source === 'header') {
    return (
      <select
        style={{ ...cellStyle, cursor: 'pointer' }}
        value={condition.value}
        onChange={e => onChange(e.target.value)}
      >
        <option value="">— select column —</option>
        {headerColumns.map(n => (
          <option key={n} value={n}>{n}</option>
        ))}
        {condition.value && !headerColumns.includes(condition.value) && (
          <option value={condition.value}>{condition.value} (missing)</option>
        )}
      </select>
    )
  }
  // Literal source — free-text input.
  return (
    <input
      type="text"
      style={cellStyle}
      value={condition.value}
      placeholder="value"
      onChange={e => onChange(e.target.value)}
    />
  )
}

// Quote a value as a SQL literal for the human-readable Advanced view.
// Single quotes are doubled; numbers and booleans pass through.
function formatSqlLiteral(v: unknown): string {
  if (v == null) return 'NULL'
  if (typeof v === 'number' || typeof v === 'boolean') return String(v)
  const s = String(v).replace(/'/g, "''")
  return `'${s}'`
}
